import { boundsOfValues } from '@/domain/construction';
import { type Bounds } from '@/domain/geometry';
import { type EntityId } from '@/domain/model';
import { type Scene } from '@/engine';

/**
 * What the camera should frame when the user asks to see their work.
 *
 * Two questions with one answer, because they differ only in which entities are counted: with a
 * selection, frame the selection; without one, frame the whole drawing. That is the rule Figma
 * settled on for its two neighbouring shortcuts, and it is right because "show me what I am
 * working on" and "show me the drawing" are the same request at two scales.
 *
 * A **selection is taken as given**, hidden entities and all. Selecting something is a statement
 * about what you want to look at, and one made deliberately — you cannot click an invisible
 * entity on the canvas, so it got there through the layers panel and the user means it.
 *
 * Framing everything counts only what is **visible**, which is not the same rule and not an
 * inconsistency. A construction usually keeps its scaffolding — the circle used to find a
 * midpoint, hidden once it had — and that scaffolding is often far outside the finished figure.
 * Counting it would shrink the drawing to make room for something nobody can see.
 *
 * `null` when nothing counted has any extent: an empty document, or one holding only lines,
 * which really do go on forever and cannot be framed. The caller then leaves the camera where it
 * is rather than flinging it at a rectangle of no size.
 */
export function framedBounds(
  scene: Scene | null,
  selection: ReadonlySet<EntityId> = new Set()
): Bounds | null {
  if (scene === null) return null;

  if (selection.size > 0) {
    const chosen = [...selection].flatMap((id) => {
      const value = scene.values.get(id);
      return value === undefined || value === null ? [] : [value];
    });
    return boundsOfValues(chosen);
  }

  const shown = [...scene.document.entities.values()].flatMap((entity) => {
    if (!entity.visible) return [];
    const value = scene.values.get(entity.id);
    return value === undefined || value === null ? [] : [value];
  });
  return boundsOfValues(shown);
}
